import React from "react";
import { GlobalBiznessLogo } from "./GlobalBiznessLogo";
import { COMPANY_CONFIG } from "../data/companyConfig";
import { Phone, Mail, MapPin, ArrowUp } from "lucide-react";

export const Footer = ({ onOpenPolicy }) => {
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <footer className="bg-slate-50 text-slate-600 text-xs border-t border-slate-200">
            {/* Upper Support Banner (Soft Blue Tint) */}
            <div className="bg-blue-50/70 py-6 px-4 sm:px-6 lg:px-8 border-b border-blue-100">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-center md:text-left">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 border border-blue-200">
                            <Phone className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-slate-900 font-bold text-sm font-['Outfit',sans-serif]">
                                Need Help or Instant Transaction Support?
                            </div>
                            <div className="text-slate-600 text-xs">
                                Reach our Hyderabad corporate desk at {COMPANY_CONFIG.phone}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <a
                            href={`tel:${COMPANY_CONFIG.phoneClean}`}
                            className="px-4 py-2 rounded-xl font-bold text-xs text-white bg-blue-600 hover:bg-blue-700 transition-colors flex items-center gap-1.5 shadow-sm shadow-blue-600/20"
                            id="footer-call-btn"
                        >
                            <Phone className="w-3.5 h-3.5" />
                            <span>Call Helpline</span>
                        </a>
                        <a
                            href={`mailto:${COMPANY_CONFIG.email}`}
                            className="px-4 py-2 rounded-xl font-bold text-xs text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-900 transition-colors flex items-center gap-1.5 border border-slate-300 shadow-xs"
                            id="footer-email-btn"
                        >
                            <Mail className="w-3.5 h-3.5" />
                            <span>{COMPANY_CONFIG.email}</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Main Footer Links & Company Details */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
                    {/* Col 1: Brand & Bio */}
                    <div className="lg:col-span-5 space-y-4">
                        <GlobalBiznessLogo size="md" variant="light" />
                        <p className="text-slate-600 leading-relaxed text-xs max-w-sm">
                            Global Bizness is a technology-driven payments and utility
                            distribution platform. We simplify essential bill payments, mobile
                            recharges, and digital merchant services for consumers and
                            businesses across India.
                        </p>
                        <div className="flex flex-wrap gap-2 pt-1">
                            <span className="px-2.5 py-1 rounded-md bg-white text-slate-700 font-semibold text-[10px] border border-slate-200">
                                256-Bit SSL Secured
                            </span>
                            <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold text-[10px] border border-emerald-200">
                                BBPS Bharat BillPay
                            </span>
                        </div>
                    </div>

                    {/* Col 2: Quick Navigation */}
                    <div className="lg:col-span-2 space-y-3">
                        <h4 className="text-slate-900 font-bold text-xs uppercase tracking-wider font-['Outfit',sans-serif]">
                            Quick Links
                        </h4>
                        <ul className="space-y-2 text-xs">
                            <li>
                                <button
                                    onClick={() => scrollTo("hero-section")}
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                    Home
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollTo("about-section")}
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                    About Us
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollTo("services-section")}
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                    Services
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollTo("why-choose-us")}
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                    Why Choose Us
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => scrollTo("contact-section")}
                                    className="hover:text-blue-600 transition-colors cursor-pointer"
                                >
                                    Contact Us
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Col 3: Legal Policies */}
                    <div className="lg:col-span-2 space-y-3">
                        <h4 className="text-slate-900 font-bold text-xs uppercase tracking-wider font-['Outfit',sans-serif]">
                            Policies &amp; Legal
                        </h4>
                        <ul className="space-y-2 text-xs">
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("privacy")}
                                    className="hover:text-blue-600 transition-colors text-left cursor-pointer"
                                    id="footer-link-privacy"
                                >
                                    Privacy Policy
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("terms")}
                                    className="hover:text-blue-600 transition-colors text-left cursor-pointer"
                                    id="footer-link-terms"
                                >
                                    Terms &amp; Conditions
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("refund")}
                                    className="hover:text-blue-700 transition-colors text-left text-blue-600 font-semibold cursor-pointer"
                                    id="footer-link-refund"
                                >
                                    Refund &amp; Cancellation
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("grievance")}
                                    className="hover:text-blue-600 transition-colors text-left cursor-pointer"
                                    id="footer-link-grievance"
                                >
                                    Grievance Officer
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("regulatory")}
                                    className="hover:text-blue-600 transition-colors text-left cursor-pointer"
                                    id="footer-link-regulatory"
                                >
                                    Regulatory &amp; Compliance
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => onOpenPolicy("security")}
                                    className="hover:text-emerald-800 transition-colors text-left text-emerald-700 font-semibold cursor-pointer"
                                    id="footer-link-security"
                                >
                                    Security Guidelines
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Col 4: Corporate Office & Contact */}
                    <div className="lg:col-span-3 space-y-3">
                        <h4 className="text-slate-900 font-bold text-xs uppercase tracking-wider font-['Outfit',sans-serif]">
                            Registered Office
                        </h4>
                        <div className="space-y-2 text-xs text-slate-600 leading-relaxed">
                            <p className="font-semibold text-slate-900">
                                {COMPANY_CONFIG.legalName}
                            </p>
                            <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                                <div>
                                    <p>{COMPANY_CONFIG.address.line1}</p>
                                    <p>{COMPANY_CONFIG.address.line2}</p>
                                    <p className="font-medium text-slate-800">
                                        {COMPANY_CONFIG.address.building}
                                    </p>
                                    <p>{COMPANY_CONFIG.address.area}</p>
                                    <p>{COMPANY_CONFIG.address.city}</p>
                                    <p>
                                        {COMPANY_CONFIG.address.state} -{" "}
                                        {COMPANY_CONFIG.address.pincode},{" "}
                                        {COMPANY_CONFIG.address.country}
                                    </p>
                                </div>
                            </div>

                            <div className="pt-2 space-y-1">
                                <div className="flex items-center gap-2">
                                    <Phone className="w-3.5 h-3.5 text-blue-600" />
                                    <a
                                        href={`tel:${COMPANY_CONFIG.phoneClean}`}
                                        className="text-slate-800 hover:text-blue-600 hover:underline"
                                    >
                                        {COMPANY_CONFIG.phone}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Mail className="w-3.5 h-3.5 text-blue-600" />
                                    <a
                                        href={`mailto:${COMPANY_CONFIG.email}`}
                                        className="text-slate-800 hover:text-blue-600 hover:underline break-all"
                                    >
                                        {COMPANY_CONFIG.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-slate-500 text-xs">
                    <div>© 2026 {COMPANY_CONFIG.legalName}. All rights reserved.</div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => onOpenPolicy("privacy")}
                            className="hover:text-blue-600 cursor-pointer"
                        >
                            Privacy
                        </button>
                        <span>•</span>
                        <button
                            onClick={() => onOpenPolicy("terms")}
                            className="hover:text-blue-600 cursor-pointer"
                        >
                            Terms
                        </button>
                        <span>•</span>
                        <button
                            onClick={() => onOpenPolicy("refund")}
                            className="hover:text-blue-600 cursor-pointer"
                        >
                            Refunds
                        </button>
                        <span>•</span>
                        <button
                            onClick={scrollToTop}
                            className="flex items-center gap-1 hover:text-blue-600 transition-colors ml-2 cursor-pointer"
                            title="Back to top"
                        >
                            <span>Top</span>
                            <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
};
